import React, { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'
import { ConfirmationModal, Table } from 'components/Ui'
import { deleteApiRecord } from 'services/FuelLevyService'
import { Success } from 'utils/alerts'
import useAuth from 'utils/hooks/useAuth'
import dayjs from 'dayjs'
import { numberFormat } from 'components/helper'
import { phoneCountryCodeOptions } from 'utils/options'
injectReducer('fuelLevy', reducer)

export default function FuelLevy() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const { data, tableData, loading } = useSelector(
        (state) => state.fuelLevy.data,
    )

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.checkBox,
    ])

    const fetchData = () => dispatch(getData({ ...tableData }))

    const handleDelete = (id) => {
        deleteApiRecord(id)
            .then(() => {
                Success('Deleted Successfully!')
                fetchData()
            })
            .catch((error) => error)
    }

    const columns = useMemo(
        () => [
            {
                field: 'company',
                header: 'Company',
                className: 'min-w-[150px]',
                renderCell: (row) => row?.assignment?.company_name,
            },
            {
                field: 'client',
                header: 'Client Company',
                className: 'min-w-[150px]',
                renderCell: (row) => row?.assignment?.client,
            },
            {
                field: 'transport_provider_name',
                header: 'Transport Provider',
                className: 'min-w-[150px]',
                renderCell: (row) => row?.assignment?.transport_provider_name,
            },
            {
                field: 'transport_provider_service',
                header: 'Service',
                className: 'min-w-[150px]',
                renderCell: (row) => row?.assignment?.service_name,
            },
            {
                field: 'from_date',
                header: 'From Date',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.from_date
                        ? dayjs(row?.from_date).format('DD-MMM-YYYY')
                        : '',
            },
            {
                field: 'to_date',
                header: 'To Date',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.to_date
                        ? dayjs(row?.to_date).format('DD-MMM-YYYY')
                        : '',
            },
            {
                field: 'percentage',
                header: 'Percentage',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    numberFormat(row.percentage, 'AUS', 2, 'percent'),
            },
            {
                field: 'percentage',
                header: 'Percentage',
                className: 'min-w-[150px]',
                renderCell: (row) => {
                    const data = phoneCountryCodeOptions.filter(
                        ({ value }) =>
                            value === row?.country_code?.replace('+', ''),
                    )
                    return (
                        <div className="flex items-center">
                            {data.length && (
                                <span>
                                    <img
                                        src={phoneCountryCodeOptions[0].imgPath}
                                        alt="Flag"
                                    />
                                </span>
                            )}
                            {row?.country_code?.includes('+')
                                ? row?.country_code
                                : `+${row?.country_code}`}
                        </div>
                    )
                },
            },
            {
                header: 'Action',
                field: 'action',
                className: 'px-4',
                renderCell: (row) => {
                    return (
                        <div className="flex items-center gap-3">
                            {checkPermission('fuel-levy', 'edit') ? (
                                <Tooltip title={'Edit'}>
                                    <Link
                                        to={`/master-data/fuel-levy/${row.id}/edit`}
                                        className="hover:text-primary"
                                    >
                                        <HiOutlinePencil size={20} />
                                    </Link>
                                </Tooltip>
                            ) : null}
                            {checkPermission('fuel-levy', 'delete') ? (
                                <Tooltip title={'Delete'}>
                                    <ConfirmationModal
                                        message={
                                            'Are you sure you want to delete this record!'
                                        }
                                        onConfirm={() => {
                                            handleDelete(row.id)
                                        }}
                                    >
                                        <span className="hover:text-danger">
                                            <HiOutlineTrash size={20} />
                                        </span>
                                    </ConfirmationModal>
                                </Tooltip>
                            ) : null}
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [
            tableData.sort,
            tableData.query,
            tableData.pageIndex,
            tableData.pageSize,
            tableData.selectedTab,
            tableData.checkBox,
        ],
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">
                    Fuel Levy
                </h2>
            </div>
            <Table
                loading={loading}
                filter={false}
                datas={data}
                columns={columns}
                add_new={
                    checkPermission('fuel-levy', 'create')
                        ? '/master-data/fuel-levy/create'
                        : ''
                }
                pagingData={tableData}
                onChange={(e) => dispatch(setTableData(e))}
            />
        </>
    )
}
